import { useCallback } from 'react';
import { AdaptoActions } from '@frontend/adapto-actions';
import { Icon } from '@frontend/icons';
import { useScopedAppFlagStore } from '@frontend/scope';
import { ContactActionPrefixes } from '@frontend/tracking-prefixes';
import { ActionButton, PopoverMenu, usePopoverMenu } from '@frontend/design-system';
import { PhoneNumberPopover } from './phone-number-popover';
import { CallSelectionContext } from './types';
import { usePhoneCallAction } from './use-call-action';

export type CallButtonProps = {
  label?: string;
  context: CallSelectionContext;
  trackingIdSuffix?: string;
};

export const CallButton = ({ label, context, trackingIdSuffix }: CallButtonProps) => {
  const { triggerProps, Modal, disabled, disabledDetails } = usePhoneCallAction({
    context,
  });

  return (
    <div>
      <ActionButton
        {...triggerProps}
        trackingId={`${ContactActionPrefixes.Call}:${trackingIdSuffix}`}
        disabled={disabled}
        label={disabledDetails ? disabledDetails : label}
      >
        <Icon name='phone-call-small' />
      </ActionButton>
      {Modal}
    </div>
  );
};

export type CallActionProps = {
  label: string;
  context: CallSelectionContext;
  trackingIdSuffix?: string;
  onClick?: () => void; // Optional onClick handler for additional functionality
};

export const CallAction = ({ label, context, trackingIdSuffix, onClick }: CallActionProps) => {
  const { triggerProps, Modal, disabled, disabledDetails, multipleContactNumbers } = usePhoneCallAction({
    context,
  });

  const { getItemProps, getTriggerProps, getMenuProps, isOpen, open } = usePopoverMenu({
    placement: 'bottom-end',
    omitSizeMiddleware: true,
  });

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const clickToCallFeatureFlag = getFeatureFlagValue('click-to-call-multiple-numbers');

  const showPhoneNumberPopover = multipleContactNumbers.length > 1 && clickToCallFeatureFlag;
  const actionTriggerProps = showPhoneNumberPopover ? { ...getTriggerProps() } : { ...triggerProps };

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    }
    if (showPhoneNumberPopover) open();
    else triggerProps?.onClick?.();
  }, [onClick, open, showPhoneNumberPopover, triggerProps]);

  return (
    <>
      <div>
        <AdaptoActions.Action
          {...actionTriggerProps}
          onClick={handleClick}
          trackingId={`${ContactActionPrefixes.Call}:${trackingIdSuffix}`}
          disabled={disabled}
          icon='phone'
          label={disabledDetails ? disabledDetails : label}
        />
        {Modal}
        {isOpen && (
          <PopoverMenu {...getMenuProps()}>
            <PhoneNumberPopover
              getItemProps={getItemProps}
              trackingIdSuffix={trackingIdSuffix}
              data={multipleContactNumbers}
              onClick={triggerProps?.onClick ?? (() => {})}
            />
          </PopoverMenu>
        )}
      </div>
    </>
  );
};
